import { select } from '@ptl/angular-redux-store';
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray } from '@angular/forms';
import { FormBroadcast, FormComponentLookup } from '@ptl/forms';
import { Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { RatingInfo } from '../../../plan-enrollment/individual-plan-enrollment-form/configs/interfaces/individualMarket.interfaceStateStructure';
import { IndividualMarketPersonsComponent, PersonFormAsJson } from '../individual-market-persons.component';

@FormComponentLookup('spousal-partner-person')
@Component({
    selector: 'app-spousal-partner',
    templateUrl: './../individual-market-persons.component.html',
    styleUrls: ['./../individual-market-persons.component.scss'],
    standalone: false
})
export class SpousalPartnerComponent extends IndividualMarketPersonsComponent
  implements OnInit, AfterViewInit, OnDestroy {

  personsArrayChanged: Subscription;
  @select(['payloadStates', 'individualMarket', 'childOnlyPolicy']) childOnlyChanges$: Observable<'Yes' | 'No'>;
  childOnlyChanged: Subscription;
  isChildOnly: boolean;

  @select(['payloadStates', 'individualMarket', 'ratingInfo', 'spousal-partner']) spousalPartnerChanges$: Observable<any>;
  spousalPartnerChanged: Subscription;

  addMoreButtonContainer: HTMLDivElement;
  isSpouseDetailsRemoved = false;

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
    this.applicantType = this.entry.custom?.spouseLabel || "Spouse";

    /** Watch for form changes, update children controls as appropriate */
    this.personsArrayChanged = this.personsForm.valueChanges
      .pipe(debounceTime(300))
      .subscribe(val => {
        this.updateAddMoreSpousalPartners();
      });

    /**
     * Watch for child-only changes
     * update ability to add more dependents and
     * to remove dependents as appropriate
     */
    this.childOnlyChanged = this.childOnlyChanges$.subscribe(val => {
      this.isChildOnly = val && val.toUpperCase() === 'YES';
      this.updateAddMoreSpousalPartners();
    });

    /** Check initial form status - update children controls as appropriate */
    this.updateAddMoreSpousalPartners();

    if (this.entry.custom?.nonSubscriberPersonsInclusiveLimit) {
      this.spousalPartnerChanged = this.spousalPartnerChanges$.subscribe(val => {
        this.onSpousalPartnerChanges();
      });

      this.dependentsChanged = this.dependentsChanges$.subscribe(val => {
        this.onDependentsChanges();
      })
    }
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();

    /** Create and interactable container */
    this.addMoreButtonContainer = this.renderer.createElement('div');

    const interactableDiv: HTMLDivElement = this.renderer.createElement('div');
    interactableDiv.style.marginTop = '15px';

    const anchor: HTMLAnchorElement = this.renderer.createElement('a');
    anchor.href = 'javascript:void(0)';
    anchor.style.display = 'flex';
    anchor.style.textDecoration = 'none';
    anchor.style.color = 'inherit';
    anchor.setAttribute('id', 'addSpouse');

    /** Icon */
    const iconSpan: HTMLSpanElement = this.renderer.createElement('span');
    iconSpan.classList.add('material-symbols-outlined');
    iconSpan.innerHTML = 'control_point';

    /** Content */
    const contentSpan: HTMLSpanElement = this.renderer.createElement('span');
    contentSpan.style.display = 'inline-block';
    contentSpan.style.margin = '-2px 0px 0px 5px';
    contentSpan.innerHTML = 'Add ' + this.applicantType;

    /** Attach */
    anchor.appendChild(iconSpan);
    anchor.appendChild(contentSpan);

    interactableDiv.appendChild(anchor);
    
    this.addMoreButtonContainer.appendChild(interactableDiv);

    (this.templateCustom.nativeElement as HTMLElement).append(
      this.addMoreButtonContainer
    );

    /** Apply onClick for adding more dependents */
    this.addMoreButtonContainer.onclick = evt => {
      this.personsFormArray.push(this.newPersonData());
      setTimeout(() => {
        const lastRemoveLink = this.removeLink.last;
        if(lastRemoveLink){
          lastRemoveLink.nativeElement.focus();
        }
      }, 100);
      this.announceMessage(this.applicantType + ' Details populated, please fill it');
    };

    this.updateAddMoreSpousalPartners();
  }

  removePerson(index: number) {
    (this.personsForm.get('persons') as UntypedFormArray).removeAt(index);
    this.announceMessage(this.applicantType + ' details deleted');
    this.isSpouseDetailsRemoved = true;
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    if (this.personsArrayChanged) {
      this.personsArrayChanged.unsubscribe();
    }
  }

  async updateAddMoreSpousalPartners(): Promise<void> {
    const atMax = await this.atMax();
    const payload = this.reducerUtilityService.getPayloadStates();
    const spouseFromPayload: { [key: string]: any } = payload?.["individualMarket"]?.["ratingInfo"]?.["spousal-partner"];
    const dependentsFromPayload: { [key: string]: any } = payload?.["individualMarket"]?.["ratingInfo"]?.["dependents"];


    /** Hide container if at max dependents */
    if (this.addMoreButtonContainer) {
      const spouseLength = (spouseFromPayload === undefined) ? 0 : Object?.keys(spouseFromPayload).length;
      const dependentLength = (dependentsFromPayload === undefined) ? 0 : Object?.keys(dependentsFromPayload).length;
      const atSpouseDependentMax = (spouseLength + dependentLength) === this.entry.custom?.nonSubscriberPersonsInclusiveLimit
      if (atMax || this.isChildOnly || atSpouseDependentMax) {
        this.addMoreButtonContainer.style.display = 'none';
      } else {
        this.addMoreButtonContainer.style.display = '';
        if(this.isSpouseDetailsRemoved){
          document.getElementById('addSpouse').focus();
          this.isSpouseDetailsRemoved = false;
        }
      }
    }
  }

  /**
   * Waits for form-listeners to complete, and then returns a comparison of
   * whether the configured maxDependent value is high-enough to add more dependents
   */
  atMax(): Promise<boolean> {
    const deferred = new Promise<boolean>((resolve, reject) => {
      setTimeout(() => {
        const maxSpousalPartners = (
          this.personsFormArray.length >=
          (this.entry.custom && this.entry.custom.maxSpousalPartners)
        );
        this.personsFormArray;
        resolve(maxSpousalPartners);
      }, 20);
    });
    return deferred;
  }
  get personsFormArray(): UntypedFormArray {
    return this.personsForm.get('persons') as UntypedFormArray;
  }

  onSpousalPartnerChanges() {
    const payload = this.reducerUtilityService.getPayloadStates();
    const spouseFromPayload: { [key: string]: any } = payload?.["individualMarket"]?.["ratingInfo"]?.["spousal-partner"];

    if (spouseFromPayload) {
      const spouseLength = Object.keys(spouseFromPayload).length;

      var event: FormBroadcast = {
        formControlName: `${this.entry.custom.customFormControlName}`,
        type: 'valueChange',
        value: spouseLength
      };
      this.formStreamTrigger.triggerFormStream(event);
    }
    this.updateAddMoreSpousalPartners();
  }

  onDependentsChanges() {
    this.updateAddMoreSpousalPartners();
  }


}
